import {
  normalizeMasterdataItemInput,
  normalizeMasterdataItemSearchInput,
  normalizeSupplierInput,
  normalizeSupplierItemInput,
  normalizeSupplierSearchInput,
  type LinkSupplierItemInput,
  type MasterdataGateway,
  type MasterdataItemPage,
  type MasterdataItemRecord,
  type MasterdataItemSearchInput,
  type SaveMasterdataItemInput,
  type SaveSupplierInput,
  type SupplierItemRecord,
  type SupplierPage,
  type SupplierRecord,
  type SupplierSearchInput,
} from '@forq/masterdata-data';
import { demoWarengruppeName } from '@forq/masters-data';

interface SupplierLink {
  readonly supplierId: string;
  readonly itemId: string;
  readonly supplierItemNumber: string;
  readonly purchasePrice: number;
  readonly leadTimeDays: number | null;
  readonly preferred: boolean;
}

interface TenantState {
  readonly items: MasterdataItemRecord[];
  readonly suppliers: SupplierRecord[];
  links: SupplierLink[];
}

function clone<T>(value: T): T { return structuredClone(value); }
function prefixFor(tenantId: string): '1' | '2' | '3' {
  if (tenantId === 'stw' || tenantId.startsWith('2222')) return '2';
  if (tenantId === 'gsl' || tenantId.startsWith('3333')) return '3';
  return '1';
}
function nextNumber(values: readonly string[], prefix: string): string {
  const counter = values.reduce((highest, value) => value.startsWith(prefix)
    ? Math.max(highest, Number(value.slice(prefix.length)) || 0)
    : highest, 0) + 1;
  return `${prefix}${String(counter).padStart(4, '0')}`;
}

function createState(tenantId: string): TenantState {
  const prefix = prefixFor(tenantId);
  const now = new Date().toISOString();
  const item = (
    id: string,
    number: string,
    kind: 'article' | 'service',
    name: string,
    values: Partial<MasterdataItemRecord>,
  ): MasterdataItemRecord => ({
    id,
    tenantId,
    number,
    kind,
    name,
    description: '',
    unit: kind === 'article' ? 'piece' : 'hour',
    salesPrice: 0,
    purchasePrice: 0,
    vatRate: 19,
    manufacturer: '',
    manufacturerPartNumber: '',
    barcode: '',
    stockTracked: kind === 'article',
    minimumStock: 0,
    active: true,
    preferredSupplierId: null,
    preferredSupplierName: '',
    supplierCount: 0,
    createdAt: now,
    updatedAt: now,
    activeSince: '2023-06-01',
    activeUntil: null,
    isUvv: false,
    ...values,
  });
  const articles = [
    item(`${tenantId}-article-1`, `${prefix}0001`, 'article', 'Hydraulikfilter Toyota', {
      description: 'Rücklauffilter für ausgewählte Toyota-Baureihen', salesPrice: 89.5, purchasePrice: 42,
      manufacturer: 'Toyota', manufacturerPartNumber: '67502-23320-71', minimumStock: 2, activeSince: '2021-05-01',
    }),
    item(`${tenantId}-article-2`, `${prefix}0002`, 'article', 'Gabelrolle 85 × 70 mm', {
      salesPrice: 68, purchasePrice: 31.5, manufacturer: 'TVH', manufacturerPartNumber: 'TVH-884201', minimumStock: 4,
    }),
    item(`${tenantId}-article-3`, `${prefix}0003`, 'article', 'Batteriestecker 160 A', {
      salesPrice: 47.9, purchasePrice: 21.4, manufacturer: 'REMA', barcode: `400${prefix}0003`, minimumStock: 3,
    }),
  ];
  const services = [
    item(`${tenantId}-service-1`, `${prefix}0001`, 'service', 'Servicetechniker', {
      description: 'Arbeitszeit Servicetechniker', unit: 'hour', salesPrice: 98,
    }),
    item(`${tenantId}-service-2`, `${prefix}0002`, 'service', 'UVV-Prüfung Flurförderzeug', {
      description: 'Jährliche Prüfung nach geltenden Vorschriften', unit: 'flat-rate', salesPrice: 145, isUvv: true,
    }),
    item(`${tenantId}-service-3`, `${prefix}0003`, 'service', 'Anfahrt', {
      description: 'Anfahrt je Kilometer', unit: 'kilometer', salesPrice: 1.25,
    }),
  ];
  const supplier = (
    id: string,
    number: string,
    name: string,
    values: Partial<SupplierRecord>,
  ): SupplierRecord => ({
    id,
    tenantId,
    number,
    name,
    vatId: '',
    email: '',
    phone: '',
    website: '',
    contactName: '',
    street: '',
    postalCode: '',
    city: '',
    countryCode: 'DE',
    notes: '',
    active: true,
    itemCount: 0,
    items: [],
    createdAt: now,
    updatedAt: now,
    activeSince: '2023-06-01',
    activeUntil: null,
    ...values,
  });
  const suppliers = [
    supplier(`${tenantId}-supplier-1`, `${prefix}0001`, 'TVH Deutschland GmbH', {
      vatId: 'DE811236411', email: 'parts@example.test', phone: '+49 000 100200',
      website: 'https://example.test', contactName: 'Ersatzteilservice', city: 'Langenhagen', postalCode: '30853',
    }),
    supplier(`${tenantId}-supplier-2`, `${prefix}0002`, 'REMA Deutschland GmbH', {
      email: 'order@example.test', city: 'Bonn', postalCode: '53111',
    }),
  ];
  const links: SupplierLink[] = [
    { supplierId: suppliers[0].id, itemId: articles[0].id, supplierItemNumber: 'TVH-67502', purchasePrice: 42, leadTimeDays: 2, preferred: true },
    { supplierId: suppliers[0].id, itemId: articles[1].id, supplierItemNumber: '884201', purchasePrice: 31.5, leadTimeDays: 3, preferred: true },
    { supplierId: suppliers[1].id, itemId: articles[2].id, supplierItemNumber: 'REMA-160', purchasePrice: 21.4, leadTimeDays: 5, preferred: true },
  ];
  return { items: [...articles, ...services], suppliers, links };
}

export class DemoMasterdataGateway implements MasterdataGateway {
  private readonly states = new Map<string, TenantState>();

  private state(tenantId: string): TenantState {
    const current = this.states.get(tenantId);
    if (current) return current;
    const created = createState(tenantId);
    this.states.set(tenantId, created);
    return created;
  }

  private enrichItem(tenantId: string, item: MasterdataItemRecord): MasterdataItemRecord {
    const state = this.state(tenantId);
    const links = state.links.filter((link) => link.itemId === item.id);
    const preferred = links.find((link) => link.preferred);
    const preferredSupplier = preferred
      ? state.suppliers.find((supplier) => supplier.id === preferred.supplierId)
      : null;
    return {
      ...item,
      preferredSupplierId: preferredSupplier?.id ?? null,
      preferredSupplierName: preferredSupplier?.name ?? '',
      supplierCount: links.length,
    };
  }

  private supplierItems(tenantId: string, supplierId: string): readonly SupplierItemRecord[] {
    const state = this.state(tenantId);
    return state.links
      .filter((link) => link.supplierId === supplierId)
      .map((link) => {
        const item = state.items.find((candidate) => candidate.id === link.itemId);
        if (!item) throw new Error('Ein verknüpfter Artikel wurde nicht gefunden.');
        return {
          itemId: item.id,
          itemNumber: item.number,
          itemName: item.name,
          supplierItemNumber: link.supplierItemNumber,
          purchasePrice: link.purchasePrice,
          leadTimeDays: link.leadTimeDays,
          preferred: link.preferred,
        };
      })
      .sort((left, right) => left.itemName.localeCompare(right.itemName, 'de'));
  }

  private enrichSupplier(tenantId: string, supplier: SupplierRecord): SupplierRecord {
    const items = this.supplierItems(tenantId, supplier.id);
    return { ...supplier, itemCount: items.length, items };
  }

  async searchItems(input: MasterdataItemSearchInput): Promise<MasterdataItemPage> {
    const normalized = normalizeMasterdataItemSearchInput(input);
    const needle = normalized.query.toLocaleLowerCase('de');
    const filtered = this.state(normalized.tenantId).items
      .filter((item) => item.kind === normalized.kind)
      .filter((item) => normalized.status === 'all' || item.active === (normalized.status === 'active'))
      .filter((item) => !needle || [item.number, item.name, item.description, item.manufacturer, item.manufacturerPartNumber, item.barcode]
        .some((value) => value.toLocaleLowerCase('de').includes(needle)))
      .filter((item) => {
        if (!normalized.bestandFrom && !normalized.bestandTo) return true;
        const from = normalized.bestandFrom || '0000-01-01';
        const to = normalized.bestandTo || '9999-12-31';
        return item.activeSince <= to && (item.activeUntil === null || item.activeUntil >= from);
      })
      .map((item) => this.enrichItem(normalized.tenantId, item))
      .sort((left, right) => left.name.localeCompare(right.name, 'de'));
    const start = (normalized.page - 1) * normalized.pageSize;
    return clone({
      items: filtered.slice(start, start + normalized.pageSize),
      total: filtered.length,
      page: normalized.page,
      pageSize: normalized.pageSize,
    });
  }

  async getItem(tenantId: string, itemId: string): Promise<MasterdataItemRecord> {
    const item = this.state(tenantId).items.find((candidate) => candidate.id === itemId);
    if (!item) throw new Error('Der Stammdatensatz wurde in diesem Mandanten nicht gefunden.');
    return clone(this.enrichItem(tenantId, item));
  }

  async saveItem(input: SaveMasterdataItemInput): Promise<MasterdataItemRecord> {
    const normalized = normalizeMasterdataItemInput(input);
    const state = this.state(normalized.tenantId);
    const existingIndex = normalized.itemId
      ? state.items.findIndex((item) => item.id === normalized.itemId)
      : -1;
    if (normalized.itemId && existingIndex < 0) throw new Error('Der Stammdatensatz wurde in diesem Mandanten nicht gefunden.');
    if (existingIndex >= 0 && state.items[existingIndex].kind !== normalized.kind) {
      throw new Error('Artikel und Dienstleistungen können nicht ineinander umgewandelt werden.');
    }
    const now = new Date().toISOString();
    const current = existingIndex >= 0 ? state.items[existingIndex] : null;
    const created: MasterdataItemRecord = {
      id: current?.id ?? `${normalized.tenantId}-${normalized.kind}-${crypto.randomUUID()}`,
      tenantId: normalized.tenantId,
      number: current?.number ?? nextNumber(
        state.items.filter((item) => item.kind === normalized.kind).map((item) => item.number),
        prefixFor(normalized.tenantId),
      ),
      kind: normalized.kind,
      name: normalized.name,
      description: normalized.description ?? '',
      unit: normalized.unit,
      salesPrice: normalized.salesPrice ?? 0,
      purchasePrice: normalized.purchasePrice ?? 0,
      vatRate: normalized.vatRate ?? 19,
      manufacturer: normalized.manufacturer ?? '',
      manufacturerPartNumber: normalized.manufacturerPartNumber ?? '',
      barcode: normalized.barcode ?? '',
      stockTracked: Boolean(normalized.stockTracked),
      minimumStock: normalized.minimumStock ?? 0,
      active: normalized.active ?? true,
      preferredSupplierId: current?.preferredSupplierId ?? null,
      preferredSupplierName: current?.preferredSupplierName ?? '',
      supplierCount: current?.supplierCount ?? 0,
      createdAt: current?.createdAt ?? now,
      updatedAt: now,
      activeSince: current?.activeSince ?? now.slice(0, 10),
      activeUntil: current?.activeUntil ?? null,
      warengruppeId: normalized.warengruppeId ?? null,
      warengruppeName: demoWarengruppeName(normalized.warengruppeId),
      isUvv: normalized.isUvv ?? false,
    };
    if (existingIndex >= 0) state.items[existingIndex] = created;
    else state.items.push(created);
    return clone(this.enrichItem(normalized.tenantId, created));
  }

  async searchSuppliers(input: SupplierSearchInput): Promise<SupplierPage> {
    const normalized = normalizeSupplierSearchInput(input);
    const needle = normalized.query.toLocaleLowerCase('de');
    const filtered = this.state(normalized.tenantId).suppliers
      .filter((supplier) => normalized.status === 'all' || supplier.active === (normalized.status === 'active'))
      .filter((supplier) => !needle || [supplier.number, supplier.name, supplier.vatId, supplier.email, supplier.city]
        .some((value) => value.toLocaleLowerCase('de').includes(needle)))
      .filter((supplier) => {
        if (!normalized.bestandFrom && !normalized.bestandTo) return true;
        const from = normalized.bestandFrom || '0000-01-01';
        const to = normalized.bestandTo || '9999-12-31';
        return supplier.activeSince <= to && (supplier.activeUntil === null || supplier.activeUntil >= from);
      })
      .map((supplier) => this.enrichSupplier(normalized.tenantId, supplier))
      .sort((left, right) => left.name.localeCompare(right.name, 'de'));
    const start = (normalized.page - 1) * normalized.pageSize;
    return clone({
      items: filtered.slice(start, start + normalized.pageSize),
      total: filtered.length,
      page: normalized.page,
      pageSize: normalized.pageSize,
    });
  }

  async getSupplier(tenantId: string, supplierId: string): Promise<SupplierRecord> {
    const supplier = this.state(tenantId).suppliers.find((candidate) => candidate.id === supplierId);
    if (!supplier) throw new Error('Der Lieferant wurde in diesem Mandanten nicht gefunden.');
    return clone(this.enrichSupplier(tenantId, supplier));
  }

  async saveSupplier(input: SaveSupplierInput): Promise<SupplierRecord> {
    const normalized = normalizeSupplierInput(input);
    const state = this.state(normalized.tenantId);
    const existingIndex = normalized.supplierId
      ? state.suppliers.findIndex((supplier) => supplier.id === normalized.supplierId)
      : -1;
    if (normalized.supplierId && existingIndex < 0) throw new Error('Der Lieferant wurde in diesem Mandanten nicht gefunden.');
    const now = new Date().toISOString();
    const current = existingIndex >= 0 ? state.suppliers[existingIndex] : null;
    const supplier: SupplierRecord = {
      id: current?.id ?? `${normalized.tenantId}-supplier-${crypto.randomUUID()}`,
      tenantId: normalized.tenantId,
      number: current?.number ?? nextNumber(state.suppliers.map((item) => item.number), prefixFor(normalized.tenantId)),
      name: normalized.name,
      vatId: normalized.vatId ?? '',
      email: normalized.email ?? '',
      phone: normalized.phone ?? '',
      website: normalized.website ?? '',
      contactName: normalized.contactName ?? '',
      street: normalized.street ?? '',
      postalCode: normalized.postalCode ?? '',
      city: normalized.city ?? '',
      countryCode: normalized.countryCode ?? 'DE',
      notes: normalized.notes ?? '',
      active: normalized.active ?? true,
      itemCount: current?.itemCount ?? 0,
      items: current?.items ?? [],
      createdAt: current?.createdAt ?? now,
      updatedAt: now,
      activeSince: current?.activeSince ?? now.slice(0, 10),
      activeUntil: current?.activeUntil ?? null,
    };
    if (existingIndex >= 0) state.suppliers[existingIndex] = supplier;
    else state.suppliers.push(supplier);
    return clone(this.enrichSupplier(normalized.tenantId, supplier));
  }

  async searchArticleOptions(tenantId: string, query = ''): Promise<readonly MasterdataItemRecord[]> {
    const needle = query.trim().toLocaleLowerCase('de');
    return clone(this.state(tenantId).items
      .filter((item) => item.kind === 'article' && item.active)
      .filter((item) => !needle || [item.number, item.name, item.manufacturerPartNumber]
        .some((value) => value.toLocaleLowerCase('de').includes(needle)))
      .slice(0, 50)
      .map((item) => this.enrichItem(tenantId, item)));
  }

  async linkSupplierItem(input: LinkSupplierItemInput): Promise<SupplierRecord> {
    const normalized = normalizeSupplierItemInput(input);
    const state = this.state(normalized.tenantId);
    const supplier = state.suppliers.find((candidate) => candidate.id === normalized.supplierId);
    if (!supplier) throw new Error('Der Lieferant wurde in diesem Mandanten nicht gefunden.');
    const item = state.items.find((candidate) => candidate.id === normalized.itemId && candidate.kind === 'article');
    if (!item) throw new Error('Der Artikel wurde in diesem Mandanten nicht gefunden.');
    if (normalized.preferred) {
      state.links = state.links.map((link) => link.itemId === item.id ? { ...link, preferred: false } : link);
    }
    const index = state.links.findIndex((link) => link.supplierId === supplier.id && link.itemId === item.id);
    const link: SupplierLink = {
      supplierId: supplier.id,
      itemId: item.id,
      supplierItemNumber: normalized.supplierItemNumber ?? '',
      purchasePrice: normalized.purchasePrice ?? 0,
      leadTimeDays: normalized.leadTimeDays ?? null,
      preferred: Boolean(normalized.preferred) || !state.links.some((candidate) => candidate.itemId === item.id),
    };
    if (index >= 0) state.links[index] = link;
    else state.links.push(link);
    return clone(this.enrichSupplier(normalized.tenantId, supplier));
  }

  async unlinkSupplierItem(tenantId: string, supplierId: string, itemId: string): Promise<SupplierRecord> {
    const state = this.state(tenantId);
    const supplier = state.suppliers.find((candidate) => candidate.id === supplierId);
    if (!supplier) throw new Error('Der Lieferant wurde in diesem Mandanten nicht gefunden.');
    const existing = state.links.find((link) => link.supplierId === supplierId && link.itemId === itemId);
    if (!existing) throw new Error('Die Lieferantenverknüpfung wurde nicht gefunden.');
    state.links = state.links.filter((link) => !(link.supplierId === supplierId && link.itemId === itemId));
    if (existing.preferred) {
      const replacement = state.links.find((link) => link.itemId === itemId);
      if (replacement) state.links = state.links.map((link) => link === replacement ? { ...link, preferred: true } : link);
    }
    return clone(this.enrichSupplier(tenantId, supplier));
  }

  /**
   * Entfernt einen Datensatz -- das Gegenstueck zum Loeschen in der Datenbank.
   * Ohne das meldet der Demo-Modus "geloescht", waehrend der Eintrag stehen bleibt.
   */
  removeItem(tenantId: string, itemId: string): void {
    const zustand = this.states.get(tenantId);
    if (!zustand) return;
    const stelle = zustand.items.findIndex((eintrag) => eintrag.id === itemId);
    if (stelle >= 0) zustand.items.splice(stelle, 1);
  }

  removeSupplier(tenantId: string, supplierId: string): void {
    const zustand = this.states.get(tenantId);
    if (!zustand) return;
    const stelle = zustand.suppliers.findIndex((eintrag) => eintrag.id === supplierId);
    if (stelle >= 0) zustand.suppliers.splice(stelle, 1);
  }
}

export function createDemoMasterdataGateway(): DemoMasterdataGateway {
  return new DemoMasterdataGateway();
}
